import React, { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { IAnimal, IBatch } from "../../modules/animals/interface";
import { useTranslate } from "../../hooks/useTranslate";
import { Delete, ExpandMore, SwapHoriz } from "@mui/icons-material";
import { useDialog } from "../../hooks/useDialog";

import image from "../../assets/jpg/no_image.jpg";

import { useItemModal, useModal } from "../../hooks/useModal";

interface IBatchTab {
  animals: IAnimal[];
  batchs: IBatch[];
  onDeleted: (ids: string[]) => void;
  onAnimalsMoved: (destiny: IBatch, animals: IAnimal[]) => void;
  onAddAnimals: (batch: IBatch, ids: string[]) => void;
  onAnimalDeleted: (ids: string[]) => void;
}

interface IBatchSelectModal {
  batch: IBatch;
  batchs: IBatch[];
  onSelected: (batch: IBatch) => void;
}

interface IAnimalSelectModal {
  batch: IBatch;
  animals: IAnimal[];
  onSelected: (animals: IAnimal[]) => void;
}

interface IAnimalItem {
  animal: IAnimal;
  checked: boolean;
  onDeleted?: (animal: IAnimal) => void;
  onAnimalMoved?: (animal: IAnimal) => void;
}

export const AnimalItem: React.FC<IAnimalItem> = ({
  animal,
  checked,
  onDeleted,
  onAnimalMoved,
}) => {
  const { translate } = useTranslate();
  return (
    <Paper key={animal.id} sx={{ p: 1, bgcolor: "grey.50" }} elevation={0}>
      <Grid container spacing={2} alignItems="center">
        <Grid item md={false}>
          <Avatar src={animal?.photo ?? image} alt={animal.name} />
        </Grid>
        <Grid item md>
          <Stack>
            <Typography>
              #{animal.number_identifier} <strong>{animal.name}</strong>
            </Typography>
            <Typography variant="caption">
              {`${translate("animals.placeholder_batch")}: ${
                animal.batch?.name ?? translate("global.uninformed")
              } / ${translate("animals.placeholder_flock")}: ${
                animal.flock?.name ?? translate("global.uninformed")
              }`}
            </Typography>
          </Stack>
        </Grid>
        <Grid item md={false}>
          {onDeleted && (
            <IconButton
              color="primary"
              onClick={() => {
                onDeleted(animal);
              }}
            >
              <Delete />
            </IconButton>
          )}
          {onAnimalMoved && (
            <Tooltip title={translate("management.move_animal_batch")}>
              <IconButton
                color="primary"
                onClick={() => {
                  onAnimalMoved(animal);
                }}
              >
                <SwapHoriz />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

const BatchSelectModal: React.FC<IBatchSelectModal> = ({
  batch,
  batchs,
  onSelected,
}) => {
  const { translate } = useTranslate();
  const modalItem = useItemModal();
  const [selected, setSelected] = useState<IBatch | null>(null);
  return (
    <Stack spacing={1} sx={{ width: 300 }}>
      {batchs
        .filter((b) => b.id !== batch.id)
        .map((batch) => (
          <Paper
            key={batch.id}
            elevation={0}
            sx={{ bgcolor: "grey.50", px: 2, py: 1 }}
          >
            <FormControlLabel
              label={batch.name}
              checked={selected?.id === batch.id}
              onChange={(_, checked) => {
                if (checked) {
                  setSelected(batch);
                } else {
                  setSelected(null);
                }
              }}
              control={<Checkbox />}
            />
          </Paper>
        ))}
      <Stack direction="row" spacing={1}>
        <Button
          fullWidth
          color="inherit"
          disableElevation
          onClick={() => {
            modalItem.close();
          }}
        >
          {translate("global.cancel")}
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          disableElevation
          disabled={!selected}
          onClick={() => {
            if (selected) {
              onSelected(selected);
              modalItem.close();
            }
          }}
        >
          {translate("animals.change_flock")}
        </Button>
      </Stack>
    </Stack>
  );
};

const AnimalSelectModal: React.FC<IAnimalSelectModal> = ({
  batch,
  animals,
  onSelected,
}) => {
  const { translate } = useTranslate();
  const modalItem = useItemModal();
  const [selected, setSelected] = useState<IAnimal[]>([]);

  return (
    <Stack spacing={1} sx={{ width: 400 }}>
      {animals.map((animal) => (
        <Paper
          key={animal.id}
          elevation={0}
          sx={{ bgcolor: "grey.50", px: 2, py: 1 }}
        >
          <FormControlLabel
            label={`#${animal.number_identifier} - ${animal.name}`}
            checked={!!selected?.find((s) => s.id === animal.id)}
            onChange={(_, checked) => {
              setSelected((old) => {
                if (checked) {
                  return [...old.filter((o) => o.id !== animal.id), animal];
                } else {
                  return [...old.filter((o) => o.id !== animal.id)];
                }
              });
            }}
            control={<Checkbox />}
          />
        </Paper>
      ))}
      <Stack direction="row" spacing={1}>
        <Button
          fullWidth
          color="inherit"
          disableElevation
          onClick={() => {
            modalItem.close();
          }}
        >
          {translate("global.cancel")}
        </Button>
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          disableElevation
          disabled={!selected.length}
          onClick={() => {
            if (selected.length > 0) {
              onSelected(selected);
              modalItem.close();
            }
          }}
        >
          {translate("animals.add_animal_batch")}
        </Button>
      </Stack>
    </Stack>
  );
};

const BatchTab: React.FC<IBatchTab> = ({
  animals,
  batchs,
  onDeleted,
  onAnimalsMoved,
  onAddAnimals,
  onAnimalDeleted,
}) => {
  const dialog = useDialog();
  const modal = useModal();
  const { translate } = useTranslate();
  const [expanded, setExpanded] = useState<string | undefined>(batchs?.[0]?.id);

  const [selectedAnimals] = useState<
    Array<{ batchId: string; animalId: string }>
  >([]);

  const isSelectedAllAnimalsByBatch = useCallback(
    (batch: IBatch) => {
      if (batch.animals.length === 0) return false;
      return batch.animals
        .map((a) => a.id)
        .reduce((is, animalId) => {
          if (
            is &&
            !selectedAnimals.find(
              (s) => s.batchId === batch.id && s.animalId === animalId
            )
          )
            return false;
          return is;
        }, true);
    },
    [selectedAnimals]
  );

  const isPartialSelectedAnimalsByBatch = useCallback(
    (batch: IBatch) => {
      return (
        !!selectedAnimals.find((s) => s.batchId === batch.id) &&
        !isSelectedAllAnimalsByBatch(batch)
      );
    },
    [selectedAnimals, isSelectedAllAnimalsByBatch]
  );

  const avaliableAnimalsByBatch = useCallback(
    (batch: IBatch) => {
      return (animals ?? []).filter(
        (a) =>
          !(batch?.animals ?? [])?.map((aa) => aa.id).includes(a.id) && !a.batch
      );
    },
    [animals]
  );

  const selectedBatchs = useMemo(() => {
    return batchs.filter(
      (b) =>
        !isPartialSelectedAnimalsByBatch(b) && isSelectedAllAnimalsByBatch(b)
    );
  }, [batchs, isPartialSelectedAnimalsByBatch, isSelectedAllAnimalsByBatch]);

  return (
    <Stack spacing={2} m={2} divider={<Divider flexItem />}>
      {(batchs ?? []).length === 0 && (
        <Paper sx={{ bgcolor: "grey.50", p: 1 }} elevation={0}>
          {translate("animals.empty_batch")}
        </Paper>
      )}
      {batchs.map((batch) => (
        <Stack key={batch.id} spacing={2}>
          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mx: 1 }}
          >
            <Stack
              component={Link}
              direction="row"
              alignItems="center"
              sx={{ flexGrow: 1 }}
              underline="none"
              color="inherit"
              onClick={() => {
                setExpanded((old) => {
                  if (expanded === batch.id) return undefined;
                  return batch.id;
                });
              }}
            >
              <ExpandMore
                sx={(theme) => ({
                  transform: `rotate(${expanded === batch.id ? 0 : -90}deg)`,
                  transition: theme.transitions.create("transform"),
                })}
              />
              <Typography
                sx={{ flexGrow: 1, fontSize: 14, verticalAlign: "middle" }}
              >
                {batch.name}
              </Typography>
              <Chip
                size="small"
                sx={{ textTransform: "lowercase" }}
                label={`${batch.count} ${translate(
                  batch.count <= 1
                    ? "animals.animal_singular"
                    : "animals.animal_plural"
                )}`}
              />
            </Stack>
            <Button
              size="small"
              // disabled={!avaliableAnimalsByBatch(batch).length }
              onClick={() => {
                modal.open({
                  title: translate("animals.title_add_animal_batch"),
                  content: (
                    <AnimalSelectModal
                      batch={batch}
                      animals={avaliableAnimalsByBatch(batch) ?? []}
                      onSelected={(selected) => {
                        onAddAnimals(
                          batch,
                          selected.map((a) => a.id)
                        );
                      }}
                    />
                  ),
                  options: {
                    maxWidth: "lg",
                  },
                });
              }}
            >
              {translate("animals.title_add_animal_batch")}
            </Button>
            <Tooltip title={translate("management.remove_batch")}>
              <div>
                <IconButton
                  color="primary"
                  size="small"
                  // disabled={batch.animals.length > 0}
                  onClick={() => {
                    dialog.open({
                      title: translate("management.title_modal_confirm_batch"),
                      message: translate(
                        "management.subtitle_modal_confirm_batch"
                      ),
                      content: (
                        <Stack
                          sx={{ mt: 3 }}
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          {selectedBatchs.map((s) => (
                            <Chip key={s.id} size="small" label={s.name} />
                          ))}
                        </Stack>
                      ),
                      buttons: [
                        {
                          label: translate("global.cancel"),
                          options: {
                            color: "inherit",
                          },
                        },
                        {
                          label: translate("global.delete"),
                          async callback() {
                            onDeleted([batch.id]);
                          },
                          options: {
                            color: "error",
                            sx: { fontWeight: "bold" },
                          },
                        },
                      ],
                    });
                  }}
                >
                  <Delete />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
          <Collapse in={expanded === batch.id} unmountOnExit>
            <Stack spacing={1}>
              {(batch?.animals ?? []).length === 0 && (
                <Paper sx={{ bgcolor: "grey.50", p: 1 }} elevation={0}>
                  {translate("animals.animals_without_batchs")}
                </Paper>
              )}
              {(batch?.animals ?? []).map((animal) => (
                <AnimalItem
                  key={animal.id}
                  animal={animal}
                  checked={
                    !!selectedAnimals.find(
                      (s) => s.animalId === animal.id && batch.id === s.batchId
                    )
                  }
                  onAnimalMoved={(animal) => {
                    modal.open({
                      title: translate("animals.choice_flock"),
                      content: (
                        <BatchSelectModal
                          batch={batch}
                          batchs={batchs}
                          onSelected={(selectedBatch) => {
                            onAnimalsMoved(selectedBatch, [animal]);
                            setExpanded(selectedBatch.id);
                          }}
                        />
                      ),
                    });
                    // onAnimalsMoved(batch, [animal]);
                  }}
                />
              ))}
            </Stack>
          </Collapse>
        </Stack>
      ))}
    </Stack>
  );
};

export default BatchTab;
